import React from 'react';
import { Box, Typography, Chip, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CoordinatePatient, CoordinationStatus, DISPOSITION_LABELS,
  STATUS_CHIP_STYLE, URGENCY_CHIP_STYLE, URGENCY_BORDER_COLOR,
} from '~/types/coordinate';

interface BoardColumn {
  label: string;
  statuses: CoordinationStatus[];
}

const COLUMNS: BoardColumn[] = [
  { label: 'Pending',           statuses: ['pending'] },
  { label: 'In Progress',       statuses: ['matching', 'matched', 'confirming'] },
  { label: 'Ready',     statuses: ['confirmed'] },
  { label: 'Awaiting Response', statuses: ['awaiting_response', 'negotiating'] },
  { label: 'Accepted',          statuses: ['accepted'] },
  { label: 'Needs Attention',   statuses: ['confirm_failed', 'declined'] },
];

interface PatientCardProps {
  patient: CoordinatePatient;
}

const PatientCard: React.FC<PatientCardProps> = ({ patient }) => {
  const navigate = useNavigate();
  const urgencyStyle = URGENCY_CHIP_STYLE[patient.urgency];
  const statusStyle = STATUS_CHIP_STYLE[patient.coordinationStatus];

  return (
    <Paper
      variant="outlined"
      onClick={() => navigate(`/coordinate/patient/${patient.id}`)}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: URGENCY_BORDER_COLOR[patient.urgency],
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
        transition: 'border-color 0.15s ease',
      }}
    >
      <Typography variant="body2" fontWeight={600} noWrap>{patient.name}</Typography>
      <Typography variant="caption" color="text.secondary">Age {patient.age} · {patient.facilityName}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
        Admit {patient.admitDate} · LOS {patient.losTargetDays}d
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          label={DISPOSITION_LABELS[patient.disposition]}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.65rem', height: 18 }}
        />
        <Chip
          label={urgencyStyle.label}
          size="small"
          sx={{ bgcolor: urgencyStyle.bgcolor, color: urgencyStyle.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
        />
      </Stack>
      <Box sx={{ mt: 0.75 }}>
        <Chip
          label={statusStyle.label}
          size="small"
          sx={{ bgcolor: statusStyle.bgcolor, color: statusStyle.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
        />
      </Box>
    </Paper>
  );
};

interface Props {
  patients: CoordinatePatient[];
}

const CoordinateBoardView: React.FC<Props> = ({ patients }) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', overflowX: 'auto', pb: 2 }}>
      {COLUMNS.map(col => {
        const colPatients = patients.filter(p => col.statuses.includes(p.coordinationStatus));
        return (
          <Box key={col.label} sx={{ minWidth: 220, maxWidth: 240, flex: '0 0 auto' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                color="primary.main"
                fontWeight={700}
                sx={{ letterSpacing: '0.08em', lineHeight: 1 }}
              >
                {col.label}
              </Typography>
              <Chip
                label={colPatients.length}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            </Stack>
            <Stack spacing={1}>
              {colPatients.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed' }}>
                  <Typography variant="caption" color="text.disabled">None</Typography>
                </Paper>
              ) : (
                colPatients.map(p => <PatientCard key={p.id} patient={p} />)
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default CoordinateBoardView;
